import CityPicker from './zipCityPicker.js';
import AdminRoster from '../views/AdminRoster.vue';
import ChangePasswordModal from '../views/ChangePasswordModal.vue';
import CompanyList from '../views/CompanyList.vue';
import CompanyToplist from '../views/CompanyToplist.vue';
import FamilyMemberList from '../views/FamilyMemberList.vue';
import FavouriteToggle from '../views/FavouriteToggle.vue';
import MyPageCard from '../views/MyPageCard.vue';
import RegisterTrip from '../views/RegisterTrip.vue';
import RouteElevationProfile from '../views/RouteElevationProfile.vue';
import RouteStats from '../views/RouteStats.vue';
import Toplist from '../views/Toplist.vue';
import { createApp } from 'vue';
import PrimeVue from 'primevue/config';
import { nbLocale } from '../config/primevueNbLocale.js';

const postAddressPickerElement = document.querySelector('[data-post-address-finder]');
if (postAddressPickerElement) {
    createApp(CityPicker).mount(postAddressPickerElement);
}

// Replaces the old standalone /user/changepassword page (see
// Xhr\MyPage\PasswordController for the endpoint it talks to).
const changePasswordMount = document.querySelector('[data-change-password-modal]');
if (changePasswordMount) {
    createApp(ChangePasswordModal, {
        csrfToken: changePasswordMount.getAttribute('data-csrf'),
    }).mount(changePasswordMount);
}

// Replaces the add/edit half of the old standalone /user/family page (see
// Xhr\MyPage\FamilyMemberController for the endpoints it talks to) - the
// whole "Familiemedlemmer" list, plus its add/edit modals, is one widget
// that fetches its own data (rather than it being rendered into the page)
// and updates itself in place after a save instead of reloading the page.
const familyMemberListMount = document.querySelector('[data-family-member-list]');
if (familyMemberListMount) {
    createApp(FamilyMemberList, {
        csrfToken: familyMemberListMount.getAttribute('data-csrf'),
    }).mount(familyMemberListMount);
}

// Replaces the plain "Fjern kobling til bedrift" links (see
// Xhr\MyPage\CompanyController for the endpoints it talks to) - same
// list/popup shape as FamilyMemberList.vue.
const companyListMount = document.querySelector('[data-company-list]');
if (companyListMount) {
    createApp(CompanyList, {
        csrfToken: companyListMount.getAttribute('data-csrf'),
    }).mount(companyListMount);
}

// The trip-code entry -> confirm modal -> success flow - see the component
// for the Xhr\MyPage\RegistrationController endpoints it talks to.
const registerTripMount = document.querySelector('[data-register-trip]');
if (registerTripMount) {
    const registerTripApp = createApp(RegisterTrip, {
        csrfToken: registerTripMount.getAttribute('data-csrf'),
        personId: parseInt(registerTripMount.getAttribute('data-person-id')),
        familyMembers: JSON.parse(registerTripMount.getAttribute('data-family-members') || '[]'),
    });
    // RegisterConfirmModal.vue's date field is a PrimeVue Calendar (same
    // component the admin date fields use) - it reads locale/ripple config
    // from this plugin, registered once here for the whole app tree.
    registerTripApp.use(PrimeVue, { ripple: true, locale: nbLocale });
    registerTripApp.mount(registerTripMount);
}

// The whole member card (person/year switcher, tabs, stats, registration
// lists, target progress/editing) lives inside MyPageCard.vue - see the
// component for the per-member/year data cache and the Xhr\MyPage\
// MemberController endpoints it talks to.
const mypageCardMount = document.querySelector('[data-mypage-card]');
if (mypageCardMount) {
    createApp(MyPageCard, {
        personId: parseInt(mypageCardMount.getAttribute('data-person-id')),
        competitionId: mypageCardMount.getAttribute('data-competition'),
        activeCompetitionName: mypageCardMount.getAttribute('data-active-competition-name'),
        csrfToken: mypageCardMount.getAttribute('data-csrf'),
        familyMembers: JSON.parse(mypageCardMount.getAttribute('data-family-members') || '[]'),
    }).mount(mypageCardMount);
}

// The route detail page's (/tur/{id}) heart icon - see
// Xhr\Frontend\User\FavouritesController for the endpoint it talks to.
const favouriteToggleMount = document.querySelector('[data-favourite-toggle]');
if (favouriteToggleMount) {
    createApp(FavouriteToggle, {
        routeId: parseInt(favouriteToggleMount.getAttribute('data-route-id')),
        csrfToken: favouriteToggleMount.getAttribute('data-csrf'),
    }).mount(favouriteToggleMount);
}

// The route detail page's toplist/visit-log widget - see
// Xhr\Frontend\Toplists\RouteToplistController and Xhr\Frontend\
// RoutesController::visits() for the endpoints it talks to.
const routeStatsMount = document.querySelector('[data-route-stats]');
if (routeStatsMount) {
    createApp(RouteStats, {
        routeId: parseInt(routeStatsMount.getAttribute('data-route-id')),
        competitionName: routeStatsMount.getAttribute('data-competition-name') || '',
    }).mount(routeStatsMount);
}

// The route detail page's elevation-profile chart, below the map - same
// component and endpoint (Xhr\Frontend\RoutesController::elevationProfile())
// as the admin edit form's chart.
const routeElevationProfileMount = document.querySelector('[data-route-elevation-profile]');
if (routeElevationProfileMount) {
    createApp(RouteElevationProfile, {
        routeId: parseInt(routeElevationProfileMount.getAttribute('data-route-id')),
    }).mount(routeElevationProfileMount);
}

// The /bedrift toplist tab - fetches its own data from Xhr\Frontend\
// Company\ToplistController, same pattern as the admin tab below.
const companyToplistMount = document.querySelector('[data-company-toplist]');
if (companyToplistMount) {
    createApp(CompanyToplist, {
        companyName: companyToplistMount.getAttribute('data-company-name'),
        competitionId: companyToplistMount.getAttribute('data-competition') ? parseInt(companyToplistMount.getAttribute('data-competition')) : null,
    }).mount(companyToplistMount);
}

// The /bedrift admin tab's roster/invite widget - see Xhr\Frontend\
// Company\AdminController for the endpoints it talks to.
const adminRosterMount = document.querySelector('[data-admin-roster]');
if (adminRosterMount) {
    createApp(AdminRoster, {
        csrfToken: adminRosterMount.getAttribute('data-csrf'),
        competitionName: adminRosterMount.getAttribute('data-competition-name') || '',
        initialRoster: JSON.parse(adminRosterMount.getAttribute('data-roster') || '[]'),
        inviteLink: adminRosterMount.getAttribute('data-invite-link') || '',
    }).mount(adminRosterMount);
}

// The /topplister page - a wrapper app that dispatches to the right
// sub-toplist (registrations/marathon/counties/companies/routes) based on
// ?type=, entirely client-side. See Xhr\Frontend\Toplists\
// CompetitionToplistController for the endpoints it talks to.
const toplistMount = document.querySelector('[data-toplist-app]');
if (toplistMount) {
    createApp(Toplist, {
        competitionId: toplistMount.getAttribute('data-competition') ? parseInt(toplistMount.getAttribute('data-competition')) : null,
        competitionName: toplistMount.getAttribute('data-competition-name') || '',
        personId: toplistMount.getAttribute('data-person-id') ? parseInt(toplistMount.getAttribute('data-person-id')) : null,
        toplists: JSON.parse(toplistMount.getAttribute('data-toplists') || '[]'),
        registrations: parseInt(toplistMount.getAttribute('data-registrations') || '0'),
        users: parseInt(toplistMount.getAttribute('data-users') || '0'),
    }).mount(toplistMount);
}
